import { useEffect } from 'react';

// ──────────────────────────────────────────────────────────────────────────────
// Sign-out (04 #6 — final form, 2026-06-12).
//
// Three session layers exist: this portal's SWA cookies, the OTHER portal's SWA
// cookies (per-domain, HttpOnly — unreachable from here), and the shared Entra
// session. HAR-verified platform behavior (2026-06-12): SWA's /.auth/logout is a
// three-stop machine — it deletes AppServiceAuthSession, chains through the Entra
// end-session ITSELF, and deletes StaticWebAppsAuthCookie only at
// /.auth/logout/complete after Entra bounces back. Entra only bounces while its
// session is alive, and the first portal's chain kills it — so exactly ONE domain
// per Microsoft session can complete the machine ("one-kill rule"). Chaining the
// other portal's /.auth/logout can therefore never work and was removed.
//
// The cross-portal/cross-device guarantee lives SERVER-SIDE instead: step 1 below
// calls POST /api/logout-all, which stamps users.sessionsRevokedAt; every API
// request from any older session then gets 403 session_revoked (withAuth), so
// leftover cookies anywhere become powerless. The chain here only has to clean
// THIS portal and the Entra session:
//
//   1. /logout (button navigates here; full-page hits fall through to this route)
//      → POST /api/logout-all (bounded, never blocks local sign-out)
//      → /.auth/logout?post_logout_redirect_uri=/signed-out  (native chain runs)
//   2. /signed-out → explicit Entra end-session hop, kept as belt-and-suspenders:
//      the native chain already ended the Entra session under observed behavior,
//      but that is platform behavior, not contract — and a surviving Entra session
//      would let a fresh silent re-auth mint a token NEWER than the revocation
//      stamp. Expected end state: Microsoft's "You signed out of your account"
//      page (a dead session does not bounce back, even with registered URIs —
//      observed in prod 2026-06-11/12). Unambiguous, fully signed out.
//
// The IDs below are public identifiers, NOT secrets (03-security-and-secrets).
// ──────────────────────────────────────────────────────────────────────────────

const TENANT_ID = 'add2b115-9cc0-4adc-8814-e6b2401b7801';
const SPA_CLIENT_ID = '2d487bc3-a2c6-4469-8c85-4f1bf2231287'; // aralere-spa-prod

/**
 * /logout — the single sign-out entry point (the AppShell button navigates here;
 * the old SWA-config redirect rule was removed so this route always runs and the
 * revocation call can never be skipped). Revoke first, then clear this portal.
 */
export function Logout(): JSX.Element {
  useEffect(() => {
    void (async () => {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 3000);
        await fetch('/api/logout-all', { method: 'POST', signal: controller.signal });
        clearTimeout(timer);
      } catch {
        /* never block local sign-out on a revocation hiccup */
      }
      window.location.replace('/.auth/logout?post_logout_redirect_uri=/signed-out');
    })();
  }, []);
  return <SigningOutMessage />;
}

/** /signed-out — this portal's cookies are gone; end the Entra session explicitly. */
export function SignedOut(): JSX.Element {
  useEffect(() => {
    const back = encodeURIComponent(`${window.location.origin}/`);
    window.location.replace(
      `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/logout?client_id=${SPA_CLIENT_ID}&post_logout_redirect_uri=${back}`,
    );
  }, []);
  return <SigningOutMessage />;
}

function SigningOutMessage(): JSX.Element {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <p className="text-sm text-[var(--color-muted)]">Signing you out…</p>
    </div>
  );
}
